'use client'

import { cn } from '@brew/ui/cn'
import { type ReactNode, useEffect, useState } from 'react'
import {
  getHeroTitlePlaybackState,
  HERO_TITLE_TEXT,
  markHeroTitlePlayed,
} from '@/components/hero-title-playback'
import {
  BrewAnimatedLogo,
  BrewStaticWordmark,
} from '@/components/icons/brew-animated-logo'

/** Both wordmark variants take this, so the box is identical either way. */
const HERO_WORDMARK_CLASSNAME = 'md:h-16 md:w-36'

const MOBILE_LINE1 = 'What emails can'
const MOBILE_LINE2 = 'I help you'
const START_DELAY_MS = 34
const FASTEST_DELAY_MS = 10
const FINAL_DELAY_MS = 28
const INITIAL_DELAY_MS = 100
const BLUR_TRAIL = 4
const EASE_OUT_AT = 0.75

function getCharDelay(index: number, total: number): number {
  const progress = index / (total - 1)

  if (progress <= EASE_OUT_AT) {
    // Accelerating: quadratic ease-in from start to fastest
    const t = progress / EASE_OUT_AT
    return START_DELAY_MS + (FASTEST_DELAY_MS - START_DELAY_MS) * t * t
  }

  // Decelerating: ease back up for the final stretch
  const t = (progress - EASE_OUT_AT) / (1 - EASE_OUT_AT)
  return FASTEST_DELAY_MS + (FINAL_DELAY_MS - FASTEST_DELAY_MS) * t * t
}

function renderTitleText(
  fullStr: string,
  visibleCount: number,
  keyPrefix: string,
  shouldAnimate: boolean
) {
  if (!shouldAnimate) {
    return fullStr
  }

  const trailStart = Math.max(0, visibleCount - BLUR_TRAIL)
  const stable = fullStr.slice(0, trailStart)
  const trail = fullStr.slice(trailStart, visibleCount)
  const hidden = fullStr.slice(visibleCount)

  return (
    <>
      {stable}
      {trail.split('').map((char, i) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: the array index IS the positional identity here
          key={`${keyPrefix}-${trailStart + i}`}
          className="animate-[char-blur-in_300ms_ease-out]"
        >
          {char}
        </span>
      ))}
      {/* opacity, not visibility:hidden — the unrevealed characters must
          stay in the server HTML and the accessibility tree. */}
      <span className="opacity-0">{hidden}</span>
    </>
  )
}

/**
 * The title row's markup, shared by the animating and the static title so a
 * loading shell and the page it precedes cannot describe two different rows.
 * Everything that decides the row's HEIGHT lives here and nowhere else.
 *
 * This row is the page heading on `/` and `/home`. It is the visible title,
 * so the heading stays in the server HTML for agents that do not run
 * JavaScript. Descendants stay phrasing content: a block element inside the
 * heading makes the HTML parser close it before the text.
 */
function HeroTitleLayout({
  className,
  charCount,
  shouldAnimate,
  /**
   * The wordmark to paint. Shells pass the inert one so a temporary tree never
   * initializes a Rive renderer it is about to throw away; both occupy the
   * identical box, so swapping between them moves nothing.
   */
  wordmark,
}: {
  className?: string
  charCount: number
  shouldAnimate: boolean
  wordmark: ReactNode
}) {
  // Mobile: line 1 types first, then line 2
  const line1Chars = Math.min(charCount, MOBILE_LINE1.length)
  // +1 accounts for the space between lines in HERO_TITLE_TEXT
  const line2Chars = Math.max(0, charCount - MOBILE_LINE1.length - 1)
  const isRiveVisible =
    !shouldAnimate || HERO_TITLE_TEXT.length - charCount <= 4

  return (
    <h1
      className={`m-0 flex flex-col items-center justify-center font-normal md:flex-row ${className ?? ''}`}
      data-hero-title-playback={shouldAnimate ? 'animate' : 'static'}
    >
      {/* Mobile: Two lines */}
      <span className="block text-center font-medium text-3xl text-foreground/80 leading-tight tracking-tighter md:hidden">
        {renderTitleText(MOBILE_LINE1, line1Chars, 'm1', shouldAnimate)}
      </span>
      <span className="flex flex-row items-center justify-center gap-0">
        <span className="block text-center font-medium text-3xl text-foreground/80 leading-tight tracking-tighter md:hidden">
          {renderTitleText(MOBILE_LINE2, line2Chars, 'm2', shouldAnimate)}
        </span>
        {/* Desktop: One line. aria-hidden because the mobile lines are the
            accessible copy; a second reading of the same sentence is noise. */}
        <span
          aria-hidden="true"
          className="hidden text-center font-medium text-foreground/80 tracking-tighter md:block md:text-4xl lg:text-4xl xl:text-4xl"
        >
          {renderTitleText(HERO_TITLE_TEXT, charCount, 'd', shouldAnimate)}
        </span>
        <span
          className={cn({
            'opacity-100': isRiveVisible,
            'opacity-0': !isRiveVisible,
            'transition-opacity duration-500': shouldAnimate,
          })}
        >
          {wordmark}
        </span>
      </span>
    </h1>
  )
}

export function HeroTitle({ className }: { className?: string }) {
  const [playback] = useState(getHeroTitlePlaybackState)
  const [charCount, setCharCount] = useState(playback.charCount)
  const [hasStarted, setHasStarted] = useState(playback.hasStarted)
  const shouldAnimate = playback.shouldAnimate

  useEffect(() => {
    if (shouldAnimate) {
      markHeroTitlePlayed()
    }
  }, [shouldAnimate])

  useEffect(() => {
    if (!shouldAnimate) {
      return
    }
    const timer = setTimeout(() => setHasStarted(true), INITIAL_DELAY_MS)
    return () => clearTimeout(timer)
  }, [shouldAnimate])

  useEffect(() => {
    if (!shouldAnimate || !hasStarted || charCount >= HERO_TITLE_TEXT.length) {
      return
    }
    const delay = getCharDelay(charCount, HERO_TITLE_TEXT.length)
    const timer = setTimeout(() => {
      setCharCount((prev) => prev + 1)
    }, delay)
    return () => clearTimeout(timer)
  }, [shouldAnimate, hasStarted, charCount])

  return (
    <HeroTitleLayout
      className={className}
      charCount={charCount}
      shouldAnimate={shouldAnimate}
      wordmark={<BrewAnimatedLogo className={HERO_WORDMARK_CLASSNAME} />}
    />
  )
}

/**
 * The finished title, with no state and no timers — what a `loading.tsx`
 * shell paints. A `static` prop on `HeroTitle` would have left the whole
 * typewriter state machine mounted in a tree that must be inert; a sibling
 * makes the inertness structural instead of something you verify by reading.
 *
 * `markPlayed` records the intro as already played, so the `HeroTitle` that
 * replaces this shell comes up finished rather than retyping a line the
 * visitor has just read. `/home`'s shell wants that. The public landing shell
 * deliberately does not: on `/` the typewriter is the first thing a visitor
 * sees.
 */
export function HeroTitleStatic({
  className,
  markPlayed = false,
}: {
  className?: string
  markPlayed?: boolean
}) {
  useEffect(() => {
    if (markPlayed) {
      markHeroTitlePlayed()
    }
  }, [markPlayed])

  return (
    <HeroTitleLayout
      className={className}
      charCount={HERO_TITLE_TEXT.length}
      shouldAnimate={false}
      wordmark={<BrewStaticWordmark className={HERO_WORDMARK_CLASSNAME} />}
    />
  )
}
